import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { api } from "../../../lib/api";
import { useAuth } from "../../../context/AuthContext";
import { toOnboardingState, type OnboardingState } from "./contracts";

export interface CompleteResult {
  ok: boolean;
  detail?: unknown;
}

export interface OnboardingContextValue {
  state: OnboardingState | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  acknowledgeTenantProfile: (done: boolean, note?: string) => Promise<void>;
  completeOnboarding: () => Promise<CompleteResult>;
  ackPending: boolean;
  completePending: boolean;
  conflictMessage: string | null;
  clearConflict: () => void;
  /** Authoritative active tenant for this browser context.
   *  Tenant users → their tenant. Platform super_admins under a support
   *  session → the target tenant on the session. Otherwise null. */
  activeTenantId: string | null;
  /** Opaque, memory-only session identity. Combines the authenticated
   *  user id with the current support-session id (if any) so that a
   *  support-target switch is treated as a new session. This value is
   *  used to scope client-side action bindings (e.g. an in-flight
   *  template-apply attempt); it is NEVER transmitted to the server. */
  sessionIdentity: string | null;
}

const OnboardingContext = createContext<OnboardingContextValue | null>(null);

interface AuthShape {
  user:
    | {
        id?: string;
        _id?: string;
        tenant_id?: string;
        scope?: string;
        platform_role?: string;
        tenant_role?: string;
      }
    | false
    | null;
  supportSession?: { id?: string; tenant_id?: string } | null;
}

/** Derive the authoritative active tenant for this browser context.
 *  Tenant user   → their tenant_id.
 *  Platform user → supportSession.tenant_id (if any) — never falls back
 *                  to a stale platform identifier.
 *  Otherwise     → null (do not fetch tenant-scoped state). */
function resolveActiveTenant(auth: AuthShape | null): string | null {
  const user = auth?.user;
  if (!user) return null;
  if (user.scope === "platform") {
    return auth?.supportSession?.tenant_id || null;
  }
  return user.tenant_id || null;
}

/** Derive an opaque session identity for the current browser context.
 *  Combines the user id with the support-session id so that ending or
 *  switching a support session is a session-identity change. Kept
 *  in-memory only — never sent to the server. */
function resolveSessionIdentity(auth: AuthShape | null): string | null {
  const user = auth?.user;
  if (!user) return null;
  // /auth/me returns ``id``; tests often use ``_id``. Accept both.
  const uid = user.id || user._id || "";
  const supportId = auth?.supportSession?.id || "";
  if (!uid && !supportId) return null;
  return `${uid}|${supportId}`;
}

/** ``OnboardingProvider`` is the single source of onboarding state for
 *  the AppShell (Setup-incomplete banner) and the Onboarding Workspace
 *  (checklist, template-selection panel, completion section).
 *
 *  Key correctness properties:
 *   • Bound to the authoritative active tenant (tenant_id for tenant
 *     users; supportSession.tenant_id for platform super_admins).
 *   • Any tenant switch (support target change / support end / logout /
 *     login as different user) immediately clears prior state and
 *     re-fetches.
 *   • A ``sessionRef`` fencing token discards any in-flight response
 *     whose owning tenant is no longer the active one — Tenant A's
 *     late-arriving GET can never overwrite Tenant B's state. */
export function OnboardingProvider({ children }: { children: ReactNode }) {
  const auth = useAuth() as AuthShape | null;
  const activeTenantId = resolveActiveTenant(auth);
  const sessionIdentity = resolveSessionIdentity(auth);
  const sessionRef = useRef<string | null>(activeTenantId);

  const [state, setState] = useState<OnboardingState | null>(null);
  const [loading, setLoading] = useState<boolean>(!!activeTenantId);
  const [error, setError] = useState<string | null>(null);
  const [ackPending, setAckPending] = useState(false);
  const [completePending, setCompletePending] = useState(false);
  const [conflictMessage, setConflictMessage] = useState<string | null>(null);
  const inflightAck = useRef(false);
  const inflightComplete = useRef(false);

  const refresh = useCallback(async () => {
    if (!activeTenantId) {
      setState(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    const localKey = sessionRef.current;
    try {
      const r = await api.get("/onboarding/state");
      if (localKey !== sessionRef.current) return; // stale — active tenant changed.
      setState(toOnboardingState(r.data));
    } catch (e) {
      const status = (e as { response?: { status?: number } })?.response?.status;
      if (status === 401) return; // handled by session-expiry singleton
      if (localKey !== sessionRef.current) return;
      setError("Unable to load onboarding status. Try Refresh.");
    } finally {
      if (localKey === sessionRef.current) setLoading(false);
    }
  }, [activeTenantId]);

  // Reset + refresh whenever the authoritative tenant changes: login,
  // logout, tenant switch, support-target switch, support end.
  useEffect(() => {
    sessionRef.current = activeTenantId;
    // Cancel any interpretation of an in-flight response for the prior
    // tenant. Clear all state derived from that tenant so no stale UI
    // remains visible for even one paint frame.
    setState(null);
    setError(null);
    setConflictMessage(null);
    inflightAck.current = false;
    inflightComplete.current = false;
    setAckPending(false);
    setCompletePending(false);
    if (activeTenantId) void refresh();
    else setLoading(false);
  }, [activeTenantId, refresh]);

  const acknowledgeTenantProfile = useCallback(
    async (done: boolean, note?: string) => {
      if (inflightAck.current) return;
      if (!activeTenantId) return;
      inflightAck.current = true;
      setAckPending(true);
      const localKey = sessionRef.current;
      try {
        const expected_version = state?.version;
        const r = await api.patch(
          "/onboarding/state/tenant_profile_confirmed",
          { done, note, expected_version },
        );
        if (localKey !== sessionRef.current) return;
        setState(toOnboardingState(r.data));
      } catch (e) {
        const status = (e as { response?: { status?: number } })?.response?.status;
        if (localKey !== sessionRef.current) return;
        if (status === 409) {
          setConflictMessage(
            "Another administrator has updated onboarding. The workspace has been refreshed.",
          );
          await refresh();
        } else {
          setError("Unable to update tenant profile confirmation.");
        }
      } finally {
        inflightAck.current = false;
        if (localKey === sessionRef.current) setAckPending(false);
      }
    },
    [refresh, state?.version, activeTenantId],
  );

  const completeOnboarding = useCallback(async (): Promise<CompleteResult> => {
    if (inflightComplete.current) return { ok: false, detail: null };
    if (!activeTenantId) return { ok: false, detail: null };
    inflightComplete.current = true;
    setCompletePending(true);
    const localKey = sessionRef.current;
    try {
      await api.post("/onboarding/complete", {});
      if (localKey !== sessionRef.current) return { ok: false, detail: null };
      await refresh();
      return { ok: true };
    } catch (e) {
      const status = (e as { response?: { status?: number } })?.response?.status;
      const detail = (e as { response?: { data?: { detail?: unknown } } })
        ?.response?.data?.detail;
      if (localKey !== sessionRef.current) return { ok: false, detail: null };
      if (status === 409) {
        setConflictMessage(
          "Another administrator has updated onboarding. The workspace has been refreshed.",
        );
      }
      await refresh();
      return { ok: false, detail };
    } finally {
      inflightComplete.current = false;
      if (localKey === sessionRef.current) setCompletePending(false);
    }
  }, [refresh, activeTenantId]);

  const clearConflict = useCallback(() => setConflictMessage(null), []);

  const value: OnboardingContextValue = {
    state,
    loading,
    error,
    refresh,
    acknowledgeTenantProfile,
    completeOnboarding,
    ackPending,
    completePending,
    conflictMessage,
    clearConflict,
    activeTenantId,
    sessionIdentity,
  };

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
}

export function useOnboarding(): OnboardingContextValue {
  const c = useContext(OnboardingContext);
  if (!c) throw new Error("useOnboarding must be used within OnboardingProvider");
  return c;
}
